import { useState, type FormEvent } from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { Category, EngineTypeKey, MachineOption, MachineSpecificationValue, Supplier } from '../../../planning/types';
import type { SettingsMachineTemplateRow } from '../../types';
import { MACHINE_TEMPLATE_MODAL_LABELS as LABELS, DETAIL_ACTION_LABELS } from '../../../../utils/labels';
import { ENGINE_TYPES, formatSpecificationLabel, SPECIFICATION_DEFINITIONS } from '../../../planning/constants';
import { useCategoryLinkedOptions } from '../../../planning/hooks/useCategoryLinkedOptions';
import { useSpecificationsForm } from '../../../planning/hooks/useSpecificationsForm';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';

interface MachineTemplateModalProps {
  template?: SettingsMachineTemplateRow;
  categories: Category[];
  suppliers: Supplier[];
  options: MachineOption[];
  onClose: () => void;
  onSave: (
    name: string,
    categoryId: string | null,
    engineType: EngineTypeKey,
    supplierId: string | null,
    optionIds: string[],
    specifications: MachineSpecificationValue[],
  ) => Promise<boolean>;
}

export default function MachineTemplateModal({
  template,
  categories,
  suppliers,
  options,
  onClose,
  onSave,
}: MachineTemplateModalProps) {
  useModalHistory(onClose);
  const [name, setName] = useState(template?.name ?? '');
  const [categoryId, setCategoryId] = useState(template?.category_id ?? '');
  const [engineType, setEngineType] = useState<EngineTypeKey>(template?.engine_type ?? 'none');
  const [supplierId, setSupplierId] = useState(template?.supplier_id ?? '');
  const [optionIds, setOptionIds] = useState<string[]>(template?.option_ids ?? []);
  const [nameError, setNameError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [saving, setSaving] = useState(false);
  const specsForm = useSpecificationsForm(template?.specifications);

  const { filteredCatalogOptions, hasNoLinkedOptions, categorySpecs, sortedCategorySpecKeys } = useCategoryLinkedOptions({
    categories,
    options,
    category: '',
    categoryId: categoryId || null,
    selectedOptionIds: optionIds,
  });

  const isCreate = !template;
  const selectedOptions = options.filter((o) => optionIds.includes(o.id));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    let hasError = false;
    if (!name.trim()) {
      setNameError(LABELS.nameRequired);
      hasError = true;
    }
    if (!categoryId) {
      setCategoryError(LABELS.categoryRequired);
      hasError = true;
    }
    if (hasError) return;

    // A specification required on the category is required here too, even though this is only
    // a suggested prefill value — a saved template with a missing required field would silently
    // create machines that fail that same requirement the moment they're actually used.
    const specsValid = specsForm.validateSpecifications(categorySpecs, sortedCategorySpecKeys, LABELS.specificationRequired);
    if (!specsValid) return;
    const specificationsPayload = specsForm.buildSpecificationsPayload(sortedCategorySpecKeys);

    setSaving(true);
    const ok = await onSave(name.trim(), categoryId, engineType, supplierId || null, optionIds, specificationsPayload);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      title={isCreate ? LABELS.createTitle : LABELS.editTitle}
      confirmLabel={LABELS.save}
      confirmFormId="machine-template-form"
      confirmDisabled={saving}
      confirmLoading={saving}
      testId="machine-template-modal"
    >
      <form id="machine-template-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <TextField
            label={LABELS.nameLabel}
            placeholder={LABELS.namePlaceholder}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            required
            autoFocus
            fullWidth
            className="mt-2"
            error={!!nameError}
            helperText={nameError || undefined}
          />

          <FormControl fullWidth required error={!!categoryError}>
            <InputLabel id="machine-template-category-label">{LABELS.categoryLabel}</InputLabel>
            <Select
              labelId="machine-template-category-label"
              label={LABELS.categoryLabel}
              value={categoryId}
              onChange={(e) => {
                const newCategoryId = e.target.value;
                setCategoryId(newCategoryId);
                if (categoryError) setCategoryError('');
                const newCategory = categories.find((c) => c.id === newCategoryId);
                specsForm.clearStaleSpecifications((newCategory?.specifications ?? []).map((s) => s.key));
              }}
              inputProps={{ 'aria-label': LABELS.categoryLabel }}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id} data-testid={`option-${c.id}`}>
                  {c.name}
                </MenuItem>
              ))}
              {categoryId && !categories.find((c) => c.id === categoryId) && (
                <MenuItem key={categoryId} value={categoryId} disabled>
                  {template?.category?.name ?? categoryId}
                </MenuItem>
              )}
            </Select>
            {categoryError && <FormHelperText>{categoryError}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="machine-template-engine-type-label">{LABELS.engineTypeLabel}</InputLabel>
            <Select
              labelId="machine-template-engine-type-label"
              label={LABELS.engineTypeLabel}
              value={engineType}
              onChange={(e) => setEngineType(e.target.value as EngineTypeKey)}
              inputProps={{ 'aria-label': LABELS.engineTypeLabel }}
            >
              {Object.entries(ENGINE_TYPES).map(([k, v]) => (
                <MenuItem key={k} value={k} data-testid={`option-engine-${k}`}>
                  {v.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {sortedCategorySpecKeys.map((k) => {
            const spec = categorySpecs.find((s) => s.key === k);
            return (
              <TextField
                key={k}
                fullWidth
                type="number"
                label={formatSpecificationLabel(k)}
                required={spec?.required}
                value={specsForm.specifications[k] ?? ''}
                onChange={(e) => specsForm.setSpecificationValue(k, e.target.value)}
                error={!!specsForm.specErrors[k]}
                helperText={specsForm.specErrors[k]}
                slotProps={{
                  htmlInput: { step: 10 ** -SPECIFICATION_DEFINITIONS[k].decimals, 'data-testid': `spec-input-${k}` },
                }}
              />
            );
          })}

          <Autocomplete
            multiple
            options={filteredCatalogOptions}
            noOptionsText={hasNoLinkedOptions ? LABELS.noLinkedOptions : LABELS.noOptionsFound}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedOptions}
            onChange={(_, newValue) => setOptionIds(newValue.map((o) => o.id))}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => {
                const { key, ...itemProps } = getItemProps({ index });
                return <Chip key={key} label={option.name} size="small" {...itemProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={LABELS.optionsLabel}
                slotProps={{
                  ...params.slotProps,
                  htmlInput: { ...params.slotProps.htmlInput, 'aria-label': LABELS.optionsLabel },
                }}
              />
            )}
          />

          <FormControl fullWidth>
            <InputLabel id="machine-template-supplier-label">{LABELS.supplierLabel}</InputLabel>
            <Select
              labelId="machine-template-supplier-label"
              label={LABELS.supplierLabel}
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              inputProps={{ 'aria-label': LABELS.supplierLabel }}
            >
              <MenuItem value="">{DETAIL_ACTION_LABELS.noSupplier}</MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={s.id} data-testid={`option-${s.id}`}>
                  {s.name}
                </MenuItem>
              ))}
              {supplierId && !suppliers.find((s) => s.id === supplierId) && (
                <MenuItem key={supplierId} value={supplierId} disabled>
                  {template?.supplier?.name ?? supplierId}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">
            {LABELS.cancel}
          </Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
